import { Component, Entity, registerComponent } from "aframe";
import { Object3D, Vector3 } from "three";
import { transformVector, untransformPoint } from "../../three/math";
import { AmmoBodyComponent, AmmoGlobal, AmmoPhysicsSystem } from "../ammo";
import { AmmoNormalComponent } from "../ammo-normal";
import { AnchorComponent, AnchorEntity } from "../anchor";
import { AvatarSystem, CollisionReaction } from "../avatar";
import { ConnectionComponent } from "../connection";
import { SlotEvent, SlotEntity } from "../slot";
import { AvatarInteractionSystem, IInteractionTarget } from "./interaction";
declare var Ammo : AmmoGlobal;

interface InteractionDetectorData
{
	collider : string[];
	hand : string;
	exclude : string[];
}
interface InteractionDetectorComponent extends Component
{
	data : InteractionDetectorData;
	reaction? : CollisionReaction;
	target? : IInteractionTarget;
}
registerComponent("interaction-detector", {
	schema : {
		collider : { type : "array", default : ["ammo-body"] },
		hand : { type : "string", default : "" },
		exclude : { type : "array", default: [] } //Array of css selectors
	},
	
	init(this : InteractionDetectorComponent)
	{
		const el = this.el;
		const scene = el.sceneEl!;

		const missingColliders = this.data.collider.filter( (attrName) => !(attrName in el.components) );
		if(missingColliders.length)
		{
			const listener = (event : Event & { detail : { name:string, id:string }}) =>
			{
				const attrName = event.detail.id ? (event.detail.name + "__" + event.detail.id) : event.detail.name;
				const ix = missingColliders.indexOf(attrName);
				if(ix < 0) return;

				missingColliders.splice(ix, 1);
				if(missingColliders.length == 0)
					el.removeEventListener("componentinitialized", listener as EventListener);

				this.update(undefined);
			};
			el.addEventListener("componentinitialized", listener);
		}


		const avatarSystem = scene.systems.avatar as AvatarSystem;

		const itemAttached = (event : SlotEvent) => {

			if(event.target !== el)
				return;

			const attachedItem = event.detail.item!, attachedAnchor = event.detail.anchor!;
			
			// Enumerate all anchors on the held item :
			const attachedAnchors = Array.from(attachedItem.querySelectorAll<AnchorEntity>("[anchor]"));
			if(attachedItem.components.anchor)
				attachedAnchors.unshift(attachedItem as AnchorEntity);

			// Enumerate all ammo-bodies that may detect those anchors :
			const attachedBodies : AmmoBodyComponent[] = [];
			for(let anchorEl of attachedAnchors)
			{
				for(let bodyEl : Entity = anchorEl; bodyEl && bodyEl.components; bodyEl = bodyEl.parentElement as Entity)
				{
					for(let key in bodyEl.components)
					{
						const bodyComp = bodyEl.components[key] as AmmoBodyComponent;
						if( (bodyComp.name == "ammo-body") && !attachedBodies.includes(bodyComp) )
							attachedBodies.push(bodyComp);
					}
					if(bodyEl === attachedItem)
						break;
				}
			}

			const attachedReaction : CollisionReaction = (phase, other, self) => 
				this.reaction && this.reaction(phase, other, self);

			const updateVrReactions = () =>
			{
				//TODO : ignore body collisions for disabled anchors.

			};

			const addVrReactions = () =>
			{
				for(let bodyComp of attachedBodies)
				{
					/* 
					const anchor = bodyComp.el.components.anchor as AnchorComponent;
					const enabled = Boolean(anchor && anchor.data.enabled);
					bodyComp.el.setAttribute(bodyComp.attrName || bodyComp.name, { enabled } ); 
					*/
					bodyComp.bodyInitialized.then( (body) => {
						avatarSystem.collisionReactions[ Ammo.getPointer(body) ] = attachedReaction;
					} );
				}

				//In case an anchors gets enabled/disabled while held, listen for changes :
				for(let anchorEl of attachedAnchors)
					anchorEl.addEventListener("interactions-changed", updateVrReactions);
			};

			const removeVrReactions = () =>
			{
				for(let anchorEl of attachedAnchors)
					anchorEl.removeEventListener("interactions-changed", updateVrReactions);

				for(let bodyComp of attachedBodies)
				{
					/*
					const anchor = bodyComp.el.components.anchor as AnchorComponent;
					const enabled = Boolean(anchor && anchor.data.enabled && anchor.fits(el));
					bodyComp.el.setAttribute(bodyComp.attrName || bodyComp.name, { enabled } );
					*/
					if(bodyComp.body)
						delete avatarSystem.collisionReactions[ Ammo.getPointer(bodyComp.body) ];
				}
			};

			scene.addEventListener("enter-vr", addVrReactions);
			scene.addEventListener("exit-vr", removeVrReactions);
			if(scene.is("vr-mode"))
				addVrReactions();

			attachedAnchor.addEventListener("detached-from-slot", () =>
			{
				removeVrReactions();
				scene.removeEventListener("enter-vr", addVrReactions);
				scene.removeEventListener("exit-vr", removeVrReactions);
			}, {once:true} );
		};


		el.addEventListener("item-attached", itemAttached as EventListener);
	},


	update(this : InteractionDetectorComponent)
	{
		const avatarOrHandEl = this.el;
		const scene = avatarOrHandEl.sceneEl!;
		const interactionSystem = scene.systems["avatar-interaction"] as AvatarInteractionSystem;
		const avatarSystem = scene.systems["avatar"] as AvatarSystem;
		const physicsSystem = scene.systems["physics"] as AmmoPhysicsSystem;

		let target : IInteractionTarget;
		if(this.data.hand == "left")
			(target = interactionSystem.leftHand).init(avatarOrHandEl as SlotEntity);
		else if(this.data.hand == "right")
			(target = interactionSystem.rightHand).init(avatarOrHandEl as SlotEntity);
		else
			target = interactionSystem;

		this.target = target;

		//There might be multiple detection bodies for the same interactor. Init, Detect and Finish only once :
		const reactionData = {
			initialized : false,
			finalized : false,
			detections : {}
		};

		const exclude = this.data.exclude || [];
		this.reaction = (phase, other, self) =>
		{
			if(phase < 0)
			{
				if(reactionData.initialized) return; //Already inited
				reactionData.initialized = true;
				reactionData.finalized = false;
				reactionData.detections = {};
				return target.reset();
			} 

			if(phase > 0)
			{
				if(reactionData.finalized) return;
				reactionData.finalized = true;
				reactionData.initialized = false;
				return target.finish(avatarSystem);
			}

			const detectorPtr = String(Ammo.getPointer(self!));
			const detectorEl = physicsSystem.components[ detectorPtr ]?.el;
			if(detectorEl === avatarOrHandEl)
			{
				//TODO : avoid detecting the item in hand
			}

			const detectedPtr = String(Ammo.getPointer(other!));
			if(detectedPtr in reactionData.detections) return; //Already detected

			const detectedBody = physicsSystem.components[ detectedPtr ];
			const detectedEl = detectedBody.el;
			(reactionData.detections as any)[detectedPtr] = detectedEl;

			if(!detectedEl) return;
			for(var x : Object3D & { el? : Entity } = detectedEl.object3D; x; x = x.parent! )
			{
				if(!x.visible)
					return; //Don't detect invisible things. TODO : disable ammo-body automatically.
				/* Moved to hand's interactionTarget.update()
					if(x.el === avatarOrHandEl)
					return; //This should not happen, but right now hands detect collisions with the objects they hold
					*/
			}
			/*
			for(var x = detectedEl; x && x.components; x = x.parentElement as Entity)
			{
				if("avatar-inventory" in x.components)
					return; //Don't detect personal items, i.e. items created inside an inventory. TODO : disable anchor/button/trigger and ammo-body automatically.
			}
			*/

			for(let selector of exclude) 
				if(detectedEl.matches(selector))
					return;
			//if(exclude.includes(detectedEl)) return; //Don't react to this excluded entity

			const ammoN : AmmoNormalComponent = detectedEl.components[ "ammo-normal" + detectedBody.attrName!.slice(detectedBody.name.length) ] as AmmoNormalComponent;
			if(ammoN)
			{
				const detectedMat = detectedEl.object3D.matrixWorld.elements;
				const thisMat = avatarOrHandEl.object3D.matrixWorld.elements;
				const N = new Vector3(ammoN.data.x, ammoN.data.y, ammoN.data.z);
				transformVector(N, detectedMat);
				const dot = (detectedMat[12] - thisMat[12]) * N.x + (detectedMat[13] - thisMat[13]) * N.y + (detectedMat[14] - thisMat[14]) * N.z;
				if(dot > 0)
					return; //Don't detect if not facing ammo-normal
			}

			//Do not react to detected hand slots (happens when holding an object and "putting" it in the other hand)
			for(let handSlot of [interactionSystem.rightHand.slot, interactionSystem.leftHand.slot])
			{
				if(detectedEl === handSlot) return;
				if(handSlot && handSlot.components.slot)
					if(detectedEl === handSlot.components.slot.data.anchor)
						return;
			}

			return target.update(detectedEl);
		};
		
		for(let attrName of this.data.collider)
		{
			const body = this.el.components[attrName] as AmmoBodyComponent;
			if(!body) continue;
			if(body.name != "ammo-body")
			{
				console.warn("Invalid collider type", body.name);
				continue;
			}
			body.bodyInitialized.then( (rb) => {
				avatarSystem.collisionReactions[ Ammo.getPointer(rb) ] = this.reaction!;
			} );
		}
	}
})